import { useState, useEffect } from 'react';
import { staffAPI, guildsAPI } from '../../api';
import api from '../../api';

const STATUS_CONFIG = {
  active: { color: 'var(--success)', label: 'Active', emoji: '🟢' },
  inactive: { color: 'var(--text-muted)', label: 'Inactive', emoji: '⚫' },
  suspended: { color: 'var(--danger)', label: 'Suspended', emoji: '🔴' },
  on_leave: { color: 'var(--warning)', label: 'On Leave', emoji: '🟡' },
};

export default function StaffPage({ guild }) {
  const [staff, setStaff] = useState([]);
  const [ranks, setRanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [saving, setSaving] = useState(false);
  const [editData, setEditData] = useState({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [memberResults, setMemberResults] = useState([]);
  const [searchingMembers, setSearchingMembers] = useState(false);
  const [addingStaff, setAddingStaff] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [newStaffRank, setNewStaffRank] = useState('');
  const [departments, setDepartments] = useState([]);
  const [staffDeptMap, setStaffDeptMap] = useState({});
  const [selectedDepts, setSelectedDepts] = useState([]);
  const [addingDept, setAddingDept] = useState(false);
  const [newDeptId, setNewDeptId] = useState('');
  const [newDeptRankId, setNewDeptRankId] = useState('');
  const [deptLoading, setDeptLoading] = useState(false);
  const sym = guild?.currency_symbol || '$';
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => () => window.removeEventListener('resize', handler);
  }, []);

  useEffect(() => {
    Promise.all([
      staffAPI.getAll(guild.id),
      guildsAPI.getRanks(guild.id),
      api.get(`/api/departments/${guild.id}`),
      api.get(`/api/departments/${guild.id}/staff-map`),
    ]).then(([staffRes, ranksRes, deptsRes, mapRes]) => {
      setStaff(staffRes.data);
      setRanks(ranksRes.data);
      setDepartments(deptsRes.data);
      setStaffDeptMap(mapRes.data);
    }).catch(console.error)
      .finally(() => setLoading(false));
  }, [guild.id]);

  const reloadDeptMap = async () => {
    try {
      const [deptsRes, mapRes] = await Promise.all([
        api.get(`/api/departments/${guild.id}`),
        api.get(`/api/departments/${guild.id}/staff-map`),
      ]);
      setDepartments(deptsRes.data);
      setStaffDeptMap(mapRes.data);
    } catch {}
  };

  const filtered = staff.filter(s => {
    const matchSearch = (s.display_name || s.username || '').toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === 'all' || s.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const handleSelect = (s) => {
    setSelected(s);
    setAddingDept(false);
    setNewDeptId('');
    setNewDeptRankId('');
    setEditData({ status: s.status, rank_id: s.rank_id, notes: s.notes || '', birthday: s.birthday ? s.birthday.split('T')[0] : '' });
    // Load this staff member's department assignments
    setSelectedDepts(staffDeptMap[s.discord_id] || []);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await staffAPI.update(guild.id, selected.discord_id, editData);
      setStaff(prev => prev.map(s => s.id === selected.id ? { ...s, ...res.data } : s));
      setSelected(prev => ({ ...prev, ...res.data }));
    } catch (err) {
      alert('Failed to save changes.');
    } finally {
      setSaving(false);
    }
  };

  const searchMembers = async (term) => {
    setMemberSearch(term);
    if (term.length < 2) { setMemberResults([]); return; }
    setSearchingMembers(true);
    try {
      const res = await api.get(`/api/discord/${guild.id}/members`, { params: { search: term } });
      setMemberResults(res.data);
    } catch {
      setMemberResults([]);
    } finally {
      setSearchingMembers(false);
    }
  };

  const handleAddStaff = async () => {
    if (!selectedMember) return alert('Please select a member first.');
    setAddingStaff(true);
    try {
      const res = await api.post(`/api/staff/${guild.id}`, {
        discord_id: selectedMember.discord_id,
        username: selectedMember.username,
        display_name: selectedMember.display_name,
        rank_id: newStaffRank || null,
        avatar_hash: selectedMember.avatar || null,
      });
      setStaff(prev => {
        const exists = prev.find(s => s.discord_id === res.data.discord_id);
        if (exists) return prev.map(s => s.discord_id === res.data.discord_id ? res.data : s);
        return [res.data, ...prev];
      });
      setShowAddModal(false);
      setSelectedMember(null);
      setMemberSearch('');
      setMemberResults([]);
      setNewStaffRank('');
    } catch (err) {
      alert('Failed to add staff: ' + (err.response?.data?.error || err.message));
    } finally {
      setAddingStaff(false);
    }
  };

  const handleRemoveStaff = async (staffMember, e) => {
    e.stopPropagation();
    if (!window.confirm(`Remove ${staffMember.display_name || staffMember.username} from staff? This deletes their record and shift history.`)) return;
    try {
      await api.delete(`/api/staff/${guild.id}/${staffMember.discord_id}`);
      setStaff(prev => prev.filter(s => s.discord_id !== staffMember.discord_id));
      if (selected?.discord_id === staffMember.discord_id) setSelected(null);
    } catch (err) {
      alert('Failed to remove staff.');
    }
  };

  if (loading) return (
    <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-muted)' }}>
      <div style={{ fontSize: '40px', marginBottom: '12px' }}>⏳</div>Loading staff...
    </div>
  );

  return (
    <div style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 360px' : '1fr', gap: '24px' }}>
      {/* Left: Staff list */}
      <div className={isMobile && selected ? 'staff-list-hidden' : ''}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
          <div>
            <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '32px', color: 'var(--text-primary)', marginBottom: '6px' }}>Staff</h1>
            <p style={{ color: 'var(--text-secondary)', fontSize: '15px' }}>{staff.length} total members</p>
          </div>
          <button onClick={() => setShowAddModal(true)} style={{
            padding: '12px 24px', background: 'var(--gradient-accent)',
            color: '#fff', border: 'none', borderRadius: '12px',
            fontSize: '14px', fontWeight: 700, cursor: 'pointer',
            fontFamily: 'var(--font-display)', boxShadow: 'var(--shadow)',
          }}>+ Add Staff</button>
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
          <input
            type="text" placeholder="Search staff..." value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              flex: 1, minWidth: '200px', padding: '10px 16px',
              border: '2px solid var(--border)', borderRadius: '10px',
              fontSize: '14px', fontFamily: 'var(--font-body)',
              background: 'var(--bg-card)', color: 'var(--text-primary)', outline: 'none',
            }}
            onFocus={e => e.target.style.borderColor = 'var(--accent-primary)'}
            onBlur={e => e.target.style.borderColor = 'var(--border)'}
          />
          {['all', 'active', 'inactive', 'suspended', 'on_leave'].map(s => (
            <button key={s} onClick={() => setFilterStatus(s)}
              style={{
                padding: '10px 16px', borderRadius: '10px', cursor: 'pointer',
                border: `2px solid ${filterStatus === s ? 'var(--accent-primary)' : 'var(--border)'}`,
                background: filterStatus === s ? 'var(--accent-soft)' : 'var(--bg-card)',
                color: filterStatus === s ? 'var(--accent-primary)' : 'var(--text-secondary)',
                fontFamily: 'var(--font-body)', fontSize: '13px', fontWeight: 600,
                transition: 'all 0.15s',
              }}>
              {s === 'all' ? 'All' : STATUS_CONFIG[s]?.label}
            </button>
          ))}
        </div>

        {/* Staff table */}
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '16px', overflow: 'hidden', boxShadow: 'var(--shadow-card)' }}>
          {/* Header */}
          <div style={{
            display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 80px',
            padding: '12px 20px', borderBottom: '1px solid var(--border)',
            fontSize: '12px', fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: '0.5px', color: 'var(--text-muted)',
          }}>
            <span>Staff Member</span><span>Rank</span><span>Period Hours</span><span>Balance</span><span>Status</span>
          </div>

          {filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>No staff found.</div>
          ) : filtered.map(s => (
            <div key={s.id} onClick={() => handleSelect(s)}
              style={{
                display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 80px',
                padding: '14px 20px', borderBottom: '1px solid var(--border)',
                cursor: 'pointer', transition: 'background 0.15s',
                background: selected?.id === s.id ? 'var(--accent-soft)' : 'transparent',
              }}
              onMouseEnter={e => { if (selected?.id !== s.id) e.currentTarget.style.background = 'var(--bg-secondary)'; }}
              onMouseLeave={e => { if (selected?.id !== s.id) e.currentTarget.style.background = 'transparent'; }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '50%', flexShrink: 0, overflow: 'hidden', position: 'relative' }}>
                  <img
                    src={`https://cdn.discordapp.com/avatars/${s.discord_id}/${s.avatar_hash}.png?size=64`}
                    alt={s.display_name || s.username}
                    style={{ width: '36px', height: '36px', borderRadius: '50%', display: 'block' }}
                    onError={e => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div style={{
                    display: 'none', width: '36px', height: '36px', borderRadius: '50%',
                    background: 'var(--gradient-accent)', position: 'absolute', top: 0, left: 0,
                    alignItems: 'center', justifyContent: 'center',
                    fontSize: '14px', fontWeight: 700, color: '#fff',
                  }}>
                    {(s.display_name || s.username || '?')[0].toUpperCase()}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)' }}>
                    {s.display_name || s.username}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                    {s.is_clocked_in ? '🟢 Clocked in' : '⚫ Offline'}
                  </div>
                  {staffDeptMap[s.discord_id]?.length > 0 && (
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '3px' }}>
                      {staffDeptMap[s.discord_id].map(d => (
                        <span key={d.dept_id} style={{
                          fontSize: '10px', padding: '1px 7px', borderRadius: '100px',
                          background: d.color + '25', color: d.color,
                          border: `1px solid ${d.color}50`, fontWeight: 700,
                        }}>{d.name}</span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', fontSize: '14px', color: 'var(--text-primary)' }}>
                {s.rank_name || <span style={{ color: 'var(--text-muted)' }}>No rank</span>}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', fontSize: '14px', color: 'var(--text-primary)' }}>
                {parseFloat(s.period_hours || 0).toFixed(1)}h
              </div>
              <div style={{ display: 'flex', alignItems: 'center', fontSize: '14px', color: 'var(--text-primary)' }}>
                {sym}{parseFloat(s.current_balance || 0).toFixed(2)}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{
                  fontSize: '11px', fontWeight: 700, padding: '3px 10px', borderRadius: '100px',
                  background: STATUS_CONFIG[s.status]?.color + '20',
                  color: STATUS_CONFIG[s.status]?.color,
                }}>
                  {STATUS_CONFIG[s.status]?.emoji} {STATUS_CONFIG[s.status]?.label}
                </span>
                <button onClick={(e) => handleRemoveStaff(s, e)} title="Remove staff"
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: 'var(--text-muted)', fontSize: '14px', padding: '2px 4px',
                    borderRadius: '4px', transition: 'color 0.15s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.color = 'var(--danger)'; }}
                  onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-muted)'; }}
                >🗑️</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Right: Staff profile editor */}
      {selected && (
        <div className="staff-profile-mobile" style={{
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: '16px', padding: '24px', height: 'fit-content',
          boxShadow: 'var(--shadow-card)', position: 'sticky', top: '80px',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              {isMobile && (
                <button onClick={() => setSelected(null)} className="mobile-back-btn"
                  style={{ display: 'none', alignItems: 'center', gap: '4px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', color: 'var(--accent-primary)', fontFamily: 'var(--font-body)', fontWeight: 600, padding: 0 }}>
                  ← Back
                </button>
              )}
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '20px', color: 'var(--text-primary)' }}>
                Edit Profile
              </h2>
            </div>
            <button onClick={() => setSelected(null)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', color: 'var(--text-muted)' }}>✕</button>
          </div>

          {/* Avatar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '24px' }}>
            <div style={{
              width: '56px', height: '56px', borderRadius: '50%',
              background: 'var(--gradient-accent)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '22px', fontWeight: 700, color: '#fff',
            }}>
              {(selected.display_name || selected.username || '?')[0].toUpperCase()}
            </div>
            <div>
              <div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--text-primary)' }}>
                {selected.display_name || selected.username}
              </div>
              <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
                Hired <span style={{ color: 'var(--text-secondary)' }}>
                  {new Date(selected.hire_date).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '24px' }}>
            {[
              { label: 'Period Hours', value: `${parseFloat(selected.period_hours || 0).toFixed(2)}h` },
              { label: 'Total Hours', value: `${parseFloat(selected.total_hours || 0).toFixed(2)}h` },
              { label: 'Balance', value: `${sym}${parseFloat(selected.current_balance || 0).toFixed(2)}` },
              { label: 'Lifetime Earned', value: `${sym}${parseFloat(selected.lifetime_earnings || 0).toFixed(2)}` },
            ].map(item => (
              <div key={item.label} style={{
                background: 'var(--bg-secondary)', borderRadius: '10px', padding: '12px',
              }}>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px' }}>{item.label}</div>
                <div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--text-primary)' }}>{item.value}</div>
              </div>
            ))}
          </div>

          {/* Edit fields */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '20px' }}>
            <div>
              <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>Status</label>
              <select value={editData.status} onChange={e => setEditData(p => ({ ...p, status: e.target.value }))}
                style={{
                  width: '100%', padding: '10px 12px',
                  border: '2px solid var(--border)', borderRadius: '10px',
                  fontSize: '14px', fontFamily: 'var(--font-body)',
                  background: 'var(--bg-card)', color: 'var(--text-primary)', outline: 'none',
                }}>
                {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                  <option key={k} value={k}>{v.emoji} {v.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>Rank</label>
              <select value={editData.rank_id || ''} onChange={e => setEditData(p => ({ ...p, rank_id: e.target.value || null }))}
                style={{
                  width: '100%', padding: '10px 12px',
                  border: '2px solid var(--border)', borderRadius: '10px',
                  fontSize: '14px', fontFamily: 'var(--font-body)',
                  background: 'var(--bg-card)', color: 'var(--text-primary)', outline: 'none',
                }}>
                <option value="">No Rank</option>
                {ranks.map(r => <option key={r.id} value={r.id}>{r.name} ({sym}{r.hourly_rate}/hr)</option>)}
              </select>
            </div>

            <div>
              <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>Notes</label>
              <textarea value={editData.notes} onChange={e => setEditData(p => ({ ...p, notes: e.target.value }))}
                placeholder="Admin notes about this staff member..."
                rows={3}
                style={{
                  width: '100%', padding: '10px 12px',
                  border: '2px solid var(--border)', borderRadius: '10px',
                  fontSize: '14px', fontFamily: 'var(--font-body)',
                  background: 'var(--bg-card)', color: 'var(--text-primary)',
                  outline: 'none', resize: 'vertical', boxSizing: 'border-box',
                }} />
            </div>

            <div>
              <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>🎂 Birthday</label>
              <input
                type="date"
                value={editData.birthday || ''}
                onChange={e => setEditData(p => ({ ...p, birthday: e.target.value || null }))}
                style={{
                  width: '100%', padding: '10px 12px',
                  border: '2px solid var(--border)', borderRadius: '10px',
                  fontSize: '14px', fontFamily: 'var(--font-body)',
                  background: 'var(--bg-card)', color: 'var(--text-primary)',
                  outline: 'none', boxSizing: 'border-box',
                }}
                onFocus={e => e.target.style.borderColor = 'var(--accent-primary)'}
                onBlur={e => e.target.style.borderColor = 'var(--border)'}
              />
              <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>
                Used for birthday announcements and bonuses if enabled in Config
              </div>
            </div>
          </div>

          {/* Departments Section */}
          <div style={{ marginBottom: '20px' }}>
            <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '10px' }}>🏢 Departments</div>
            {selectedDepts.length === 0 ? (
              <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '8px' }}>Not assigned to any department.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '10px' }}>
                {selectedDepts.map(d => (
                  <div key={d.dept_id} style={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    padding: '8px 12px', borderRadius: '8px',
                    background: d.color + '15', border: `1px solid ${d.color}40`,
                  }}>
                    <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: d.color, flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' }}>{d.name}</div>
                      {d.rank_name && <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{d.rank_name}</div>}
                    </div>
                    <button onClick={async () => {
                      try {
                        await api.delete(`/api/departments/${guild.id}/staff/${selected.discord_id}/remove`, { data: { department_id: d.dept_id } });
                        await reloadDeptMap();
                        setSelectedDepts(prev => prev.filter(x => x.dept_id !== d.dept_id));
                      } catch { alert('Failed to remove from department.'); }
                    }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '14px', padding: '2px 4px' }}
                      onMouseEnter={e => e.currentTarget.style.color = 'var(--danger)'}
                      onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}>✕</button>
                  </div>
                ))}
              </div>
            )}

            {addingDept ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '12px', background: 'var(--bg-secondary)', borderRadius: '10px' }}>
                <select value={newDeptId} onChange={e => setNewDeptId(e.target.value)}
                  style={{ width: '100%', padding: '8px 10px', border: '2px solid var(--border)', borderRadius: '8px', fontSize: '13px', fontFamily: 'var(--font-body)', background: 'var(--bg-card)', color: 'var(--text-primary)', outline: 'none' }}>
                  <option value="">Select department...</option>
                  {departments.filter(d => !selectedDepts.find(s => s.dept_id === d.id)).map(d => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
                {newDeptId && (
                  <select value={newDeptRankId} onChange={e => setNewDeptRankId(e.target.value)}
                    style={{ width: '100%', padding: '8px 10px', border: '2px solid var(--border)', borderRadius: '8px', fontSize: '13px', fontFamily: 'var(--font-body)', background: 'var(--bg-card)', color: 'var(--text-primary)', outline: 'none' }}>
                    <option value="">No rank</option>
                    {(departments.find(d => d.id === parseInt(newDeptId))?.locked_ranks || ranks).map(r => (
                      <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                  </select>
                )}
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button disabled={!newDeptId || deptLoading} onClick={async () => {
                    setDeptLoading(true);
                    try {
                      await api.post(`/api/departments/${guild.id}/staff/${selected.discord_id}/assign`, {
                        department_id: parseInt(newDeptId),
                        rank_id: newDeptRankId ? parseInt(newDeptRankId) : null,
                      });
                      await reloadDeptMap();
                      const mapRes = await api.get(`/api/departments/${guild.id}/staff-map`);
                      setSelectedDepts(mapRes.data[selected.discord_id] || []);
                      setStaffDeptMap(mapRes.data);
                      setAddingDept(false);
                      setNewDeptId('');
                      setNewDeptRankId('');
                    } catch (err) {
                      alert(err?.response?.data?.error || 'Failed to assign department.');
                    } finally { setDeptLoading(false); }
                  }} style={{ flex: 1, padding: '8px', background: 'var(--gradient-accent)', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: !newDeptId || deptLoading ? 'not-allowed' : 'pointer', fontFamily: 'var(--font-display)', opacity: !newDeptId ? 0.5 : 1 }}>
                    {deptLoading ? '...' : 'Assign'}
                  </button>
                  <button onClick={() => { setAddingDept(false); setNewDeptId(''); setNewDeptRankId(''); }}
                    style={{ padding: '8px 14px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '13px', cursor: 'pointer', color: 'var(--text-secondary)' }}>Cancel</button>
                </div>
              </div>
            ) : (
              <button onClick={() => setAddingDept(true)}
                style={{ width: '100%', padding: '8px', background: 'transparent', border: '1px dashed var(--border)', borderRadius: '8px', fontSize: '13px', color: 'var(--text-muted)', cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
                + Add to Department
              </button>
            )}
          </div>

          <button onClick={handleSave} disabled={saving}
            style={{
              width: '100%', padding: '12px',
              background: saving ? 'var(--text-muted)' : 'var(--gradient-accent)',
              color: '#fff', border: 'none', borderRadius: '10px',
              fontSize: '15px', fontWeight: 700, fontFamily: 'var(--font-display)',
              cursor: saving ? 'not-allowed' : 'pointer',
              transition: 'opacity 0.15s',
            }}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      )}

      {/* Add Staff Modal */}
      {showAddModal && (
        <div
          onClick={() => setShowAddModal(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'rgba(0,0,0,0.4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <div onClick={e => e.stopPropagation()} style={{
            background: 'var(--bg-card)', borderRadius: '20px', padding: '28px',
            maxWidth: '480px', width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
            border: '1px solid var(--border)', maxHeight: '80vh', overflowY: 'auto',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '22px', color: 'var(--text-primary)' }}>Add Staff Member</h2>
              <button onClick={() => setShowAddModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px', color: 'var(--text-muted)' }}>✕</button>
            </div>

            {/* Member search */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>
                Search Discord Member
              </label>
              <input
                type="text"
                placeholder="Type a name to search..."
                value={memberSearch}
                onChange={e => searchMembers(e.target.value)}
                style={{
                  width: '100%', padding: '10px 12px', boxSizing: 'border-box',
                  border: '2px solid var(--border)', borderRadius: '10px',
                  fontSize: '14px', fontFamily: 'var(--font-body)',
                  background: 'var(--bg-card)', color: 'var(--text-primary)', outline: 'none',
                }}
                onFocus={e => e.target.style.borderColor = 'var(--accent-primary)'}
                onBlur={e => e.target.style.borderColor = 'var(--border)'}
              />
            </div>

            {/* Search results */}
            {searchingMembers && <div style={{ fontSize: '13px', color: 'var(--text-muted)', padding: '8px' }}>Searching...</div>}
            {!searchingMembers && memberResults.length > 0 && !selectedMember && (
              <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid var(--border)', borderRadius: '10px', marginBottom: '16px' }}>
                {memberResults.map(m => (
                  <div key={m.discord_id} onClick={() => { setSelectedMember(m); setMemberResults([]); }}
                    style={{
                      padding: '10px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px',
                      borderBottom: '1px solid var(--border)', transition: 'background 0.15s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-secondary)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <img
                      src={m.avatar ? `https://cdn.discordapp.com/avatars/${m.discord_id}/${m.avatar}.png?size=64` : `https://cdn.discordapp.com/embed/avatars/0.png`}
                      alt={m.display_name}
                      style={{ width: '32px', height: '32px', borderRadius: '50%' }}
                    />
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)' }}>{m.display_name}</div>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>@{m.username}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Selected member */}
            {selectedMember && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: '12px',
                padding: '12px 14px', background: 'var(--accent-soft)', borderRadius: '10px',
                marginBottom: '16px', border: '1px solid var(--accent-primary)',
              }}>
                <img
                  src={selectedMember.avatar ? `https://cdn.discordapp.com/avatars/${selectedMember.discord_id}/${selectedMember.avatar}.png?size=64` : `https://cdn.discordapp.com/embed/avatars/0.png`}
                  alt={selectedMember.display_name}
                  style={{ width: '40px', height: '40px', borderRadius: '50%' }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-primary)' }}>{selectedMember.display_name}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>@{selectedMember.username}</div>
                </div>
                <button onClick={() => { setSelectedMember(null); setMemberSearch(''); }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent-primary)', fontSize: '13px' }}>Change</button>
              </div>
            )}

            {/* Rank select */}
            {selectedMember && (
              <div style={{ marginBottom: '20px' }}>
                <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>
                  Assign Rank (optional)
                </label>
                <select value={newStaffRank} onChange={e => setNewStaffRank(e.target.value)}
                  style={{
                    width: '100%', padding: '10px 12px', border: '2px solid var(--border)', borderRadius: '10px',
                    fontSize: '14px', fontFamily: 'var(--font-body)', background: 'var(--bg-card)', color: 'var(--text-primary)', outline: 'none',
                  }}>
                  <option value="">No Rank</option>
                  {ranks.map(r => <option key={r.id} value={r.id}>{r.name} ({sym}{r.hourly_rate}/hr)</option>)}
                </select>
              </div>
            )}

            <button onClick={handleAddStaff} disabled={!selectedMember || addingStaff} style={{
              width: '100%', padding: '12px',
              background: (!selectedMember || addingStaff) ? 'var(--text-muted)' : 'var(--gradient-accent)',
              color: '#fff', border: 'none', borderRadius: '10px',
              fontSize: '15px', fontWeight: 700, fontFamily: 'var(--font-display)',
              cursor: (!selectedMember || addingStaff) ? 'not-allowed' : 'pointer',
            }}>
              {addingStaff ? 'Adding...' : 'Add to Staff'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
